import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import PlayBtn from '../ui/PlayBtn';
import { formatDuration } from '../../utils/VideoUtils';
import { formatTimeAgo } from '../../utils/DateUtils';
import type { IRecomendVideo } from '../../model/home';
import type { SubjectColor } from './MyVideoCard';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

export interface CommonVideoCardProps {
  video: IRecomendVideo;
  index: number;
  subjectColorMap: Record<string, SubjectColor>;
  onVideoClick: (videoId: string) => void;
}

export function CommonVideoCard({
  video,
  index,
  subjectColorMap,
  onVideoClick,
}: CommonVideoCardProps) {
  const videoSubject = video.subject || 'Others';
  const subjectColor = subjectColorMap[videoSubject]
    ? subjectColorMap[videoSubject]
    : subjectColorMap['Others'];

  return (
    <Card
      onClick={() => onVideoClick(video.id)}
      className="w-[280px] gap-2 m-2 overflow-hidden transition-all duration-300 cursor-pointer hover:bg-accent animate-in fade-in slide-in-from-bottom-4 border-none shadow-none"
      style={{
        animationDelay: `${index * 50}ms`,
        border: 'none',
        boxShadow: 'none',
      }}
    >
      {/* Thumbnail */}
      <div
        className="relative aspect-video flex items-center justify-center overflow-hidden border"
        style={{
          background: `url(${video.cover_img || 'https://cdn.jumpstar.app/solve/ai_gen/20251201/9f2c75b4-6e7d-4380-9ca5-c88d066b5c1f.png'}) center center / 100% no-repeat`,
          borderRadius: '12px',
        }}
      >
        <PlayBtn />

        {/* Duration */}
        <div className="absolute bottom-2 right-2">
          <div className="bg-black/30 text-white text-xs px-2 py-0.5 rounded">
            {formatDuration(Math.floor(video.video_duration))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-3 pt-0.5 flex flex-col h-[86px]">
        {/* Subject Badge and Creation Time */}
        <div className="flex items-center justify-between mb-2">
          <Badge
            className={`${subjectColor.bg} ${subjectColor.text} rounded-full px-3 py-0.5 border border-black/5`}
          >
            {videoSubject}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatTimeAgo(video.createdAt)}
          </span>
        </div>
        {/* Title - always takes 2 lines space */}
        <h4 className="mb-2 line-clamp-2 min-h-12">
          <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[rehypeKatex]}
          >
            {video.question}
          </ReactMarkdown>
        </h4>

        <div className="grow"></div>
      </div>
    </Card>
  );
}
